import type { CollisionDetection, UniqueIdentifier } from "@dnd-kit/core";

/**
 * "Deepest droppable under the cursor" collision detection that
 * deliberately ignores dnd-kit's cached `droppableRects` and re-reads
 * each container's bounding rect on every pass.
 *
 * Why we don't use dnd-kit's built-in `pointerWithin`:
 *
 *  - The canvas explorer keeps the inactive scope mounted via
 *    `display:none` so a cross-scope drag survives a scope flip
 *    (dnd-kit cancels a drag the moment its source element unmounts).
 *  - When that scope toggles back to visible mid-drag, the folder
 *    droppables it contains are now laid out with real rects, but
 *    dnd-kit's cache still holds the zero rects it captured while they
 *    were hidden. `MeasuringStrategy.Always` is documented to refresh
 *    on every drag move, but in practice it doesn't re-measure
 *    droppables whose ancestor visibility changes — it only refreshes
 *    on registration / window resize.
 *  - The result is that the workspace body's (correctly-cached) rect
 *    matches every cursor position over the tree while the folder
 *    rows' (stale-zero) rects match nothing, so root always wins.
 *
 * Live `getBoundingClientRect` reads cost a layout flush per droppable
 * per move. With ~10 droppables in the explorer that's negligible
 * (single-digit microseconds), and the correctness win is decisive.
 *
 * Returns matches sorted ascending by area so the deepest (smallest)
 * target wins when multiple rects contain the cursor — what every
 * file-manager DnD does.
 *
 * Anti-flicker hysteresis: rail rows sit a couple of pixels apart, so a
 * pointer transiting from one row to the next briefly hits only their
 * enclosing section — which made the whole section flash between per-row
 * highlights. When the pointer has just left a small target and the new
 * winner is a container that ENCLOSES it, we keep answering with the old
 * target for a short grace period. Deliberately hovering the container
 * outlasts the grace and highlights it as before; deepest-wins switching
 * (section → folder under the pointer) stays instant.
 */
const STICKY_GRACE_MS = 120;
/** A silent gap this long means a new drag — drop the sticky memory. */
const STICKY_RESET_MS = 400;

interface StickyMemory {
  id: UniqueIdentifier;
  rect: DOMRect;
  /** Last time the pointer was actually inside the sticky target. */
  insideAt: number;
  /** Last time the collision function ran at all. */
  calledAt: number;
}

let sticky: StickyMemory | null = null;

function encloses(outer: DOMRect, inner: DOMRect): boolean {
  const tolerance = 1;
  return (
    outer.left <= inner.left + tolerance &&
    outer.right >= inner.right - tolerance &&
    outer.top <= inner.top + tolerance &&
    outer.bottom >= inner.bottom - tolerance
  );
}

export const deepestPointerWithin: CollisionDetection = ({
  droppableContainers,
  pointerCoordinates,
}) => {
  if (!pointerCoordinates) return [];
  const { x, y } = pointerCoordinates;
  const now = Date.now();
  if (sticky && now - sticky.calledAt > STICKY_RESET_MS) sticky = null;
  if (sticky) sticky.calledAt = now;

  let hits: Array<{
    id: UniqueIdentifier;
    data: Record<string, unknown>;
    area: number;
    rect: DOMRect;
    node: HTMLElement;
    occludes: boolean;
  }> = [];

  for (const container of droppableContainers) {
    if (container.disabled) continue;
    const node = container.node?.current as HTMLElement | undefined;
    if (!node) continue;
    // `offsetParent === null` short-circuits the rect read for any
    // element whose ancestor is `display:none`. Skips the synchronous
    // layout flush for hidden droppables.
    if (!node.offsetParent && node !== document.body) continue;
    const rect = node.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) continue;
    if (x < rect.left || x > rect.right) continue;
    if (y < rect.top || y > rect.bottom) continue;
    hits.push({
      id: container.id,
      data: { droppableContainer: container, value: 0 },
      area: rect.width * rect.height,
      rect,
      node,
      occludes: container.data.current?.occludes === true,
    });
  }

  // A droppable marked `occludes` floats OVER the page (the floating chat
  // window). Smallest-rect-wins would let a covered target underneath it
  // steal the drop, so when the pointer is inside an occluder, only hits
  // within an occluder's DOM subtree stay in play.
  if (hits.some((h) => h.occludes)) {
    const occluders = hits.filter((h) => h.occludes);
    hits = hits.filter((h) => occluders.some((o) => o.node.contains(h.node)));
  }

  if (hits.length === 0) {
    // Pointer over nothing: the last target may still be mid-grace (a row gap
    // also briefly matches no row), but with no enclosing hit there's nothing
    // to suppress — just report empty.
    return [];
  }

  hits.sort((a, b) => a.area - b.area);
  const best = hits[0];

  const bestIsSticky = sticky && best.id === sticky.id;
  if (bestIsSticky || !sticky || hits.some((h) => h.id === sticky!.id)) {
    // The pointer is (still) inside the winner or the remembered target —
    // normal deepest-wins behavior, refresh the memory from the winner.
    sticky = { id: best.id, rect: best.rect, insideAt: now, calledAt: now };
    return hits.map(({ id, data }) => ({ id, data }));
  }

  // The pointer just left the remembered target and the new winner encloses
  // it (e.g. folder row → its section while crossing the gap to the next
  // row). Bridge with the old target briefly so the section doesn't flash.
  if (
    now - sticky.insideAt <= STICKY_GRACE_MS &&
    encloses(best.rect, sticky.rect)
  ) {
    const container = droppableContainers.find((c) => c.id === sticky!.id);
    if (container && !container.disabled) {
      return [
        { id: sticky.id, data: { droppableContainer: container, value: 0 } },
        ...hits.map(({ id, data }) => ({ id, data })),
      ];
    }
  }

  sticky = { id: best.id, rect: best.rect, insideAt: now, calledAt: now };
  return hits.map(({ id, data }) => ({ id, data }));
};
